import React, { useState } from 'react';
import { useStore } from '../../context/StoreContext';
import { useNavigate } from 'react-router-dom';

export default function CheckoutModal({ onClose }) {
  const { cart, userEmail, createOrder, setCartOpen, showToast } = useStore();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('address'); // address, payment, success
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const total = subtotal; // free shipping
  const deliveryEstimate = '3-5 business days';
  
  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (!address || !phone) {
      showToast('Please fill all fields');
      return;
    }
    setStep('payment');
  };
  
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const orderData = {
      address,
      phone,
      paymentMethod,
      items: cart.map(item => ({
        id: item.id,
        name: item.name,
        brand: item.brand,
        price: item.price,
        quantity: item.qty,
        size: item.size,
        image: item.img,
      })),
      total,
      deliveryEstimate,
    };
    
    const order = await createOrder(orderData);
    setLoading(false);
    setStep('success');
    
    // Close cart drawer and checkout modal after success
    setTimeout(() => {
      setCartOpen(false);
      onClose();
      navigate('/order-history');
    }, 2000);
  };
  
  if (step === 'success') {
    return (
      <div className="fixed inset-0 z-[150] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
        <div className="bg-white rounded-2xl p-8 text-center max-w-md mx-4">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
            <i className="ri-check-line text-3xl text-green-600"></i>
          </div>
          <h3 className="text-2xl font-bold mt-4">Order Placed!</h3>
          <p className="text-slate-600 mt-2">Thank you for your purchase.</p>
          <p className="text-sm text-slate-500">Confirmation sent to {userEmail}</p>
          <button onClick={() => navigate('/order-history')} className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-xl">
            View Orders
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-[150] overflow-y-auto">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
      <div className="relative max-w-lg mx-auto mt-20 mb-20 bg-white rounded-2xl shadow-2xl p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400"><i className="ri-close-line text-xl"></i></button>
        
        <h2 className="text-2xl font-bold mb-6">Checkout</h2>
        
        {step === 'address' && (
          <form onSubmit={handleAddressSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-1">Delivery Address</label>
              <textarea 
                rows="3" 
                className="w-full border rounded-xl p-3 focus:border-indigo-500 outline-none"
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="House No., Street, City, Pincode"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-1">Phone Number</label>
              <input 
                type="tel" 
                className="w-full border rounded-xl p-3 focus:border-indigo-500 outline-none"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold">Continue to Payment</button>
          </form>
        )}
        
        {step === 'payment' && (
          <form onSubmit={handlePaymentSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Payment Method</label>
              <div className="space-y-2">
                {[
                  { value: 'card', label: '💳 Credit / Debit Card' },
                  { value: 'upi', label: '📱 UPI (Google Pay, PhonePe, etc.)' },
                  { value: 'qr', label: '📷 Scan QR Code' },
                  { value: 'cod', label: '💰 Cash on Delivery' }
                ].map(m => (
                  <label key={m.value} className="flex items-center gap-3 p-3 border rounded-xl cursor-pointer">
                    <input 
                      type="radio" 
                      name="payment" 
                      value={m.value}
                      checked={paymentMethod === m.value}
                      onChange={() => setPaymentMethod(m.value)}
                      className="accent-indigo-600"
                    />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-xl mb-4">
              <h4 className="font-semibold">Order Summary</h4>
              <div className="flex justify-between text-sm mt-2">
                <span>Subtotal</span><span>₹{total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Delivery</span><span>FREE</span>
              </div>
              <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                <span>Total</span><span>₹{total.toLocaleString()}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">Estimated delivery: {deliveryEstimate}</p>
            </div>
            
            <button type="submit" disabled={loading} className="w-full bg-slate-900 text-white py-3 rounded-xl font-semibold disabled:opacity-50">
              {loading ? 'Processing...' : `Pay ₹${total.toLocaleString()}`}
            </button>
            <p className="text-xs text-center text-slate-500 mt-3">*Demo payment – no real charge</p>
          </form>
        )}
      </div>
    </div>
  );
}